import S from './BasicInfoEdit.module.scss';
import C from '../../Resume.module.scss';
import '../../../../../styles/customCalendar.scss';
import removeIcon from '../../assets/remove.svg';
import 'react-datepicker/dist/react-datepicker.css';

import React, {
  Dispatch,
  InputHTMLAttributes,
  SetStateAction,
  FocusEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { uploadImage } from '../../../../../repository/ResumeRepository';
import { useRecoilValue } from 'recoil';
import { IvyAuthorizationTokenAtom } from '../../../../../recoil/Authorization.recoil';
import _ from 'lodash';
import { AuthUtils } from '../../../../../utils/AuthUtils';
import { InputUtils } from '../../../../../utils/InputUtils';
import { ProfileType, ResumeType } from '../../../../../types/ResumeTypes';
import { MedeaType } from '../../../../../types/MedeaTypes';
import { DateTimeUtils } from '../../../../../utils/DateTimeUtils';
import SelectDateBox from '../../../../common/SelectDateBox';
import { useDaumPostcodePopup } from 'react-daum-postcode';
import { ResumeTabsType } from '../../../../../types/ResumeTypes';
import SearchDropdown from "../../../../common/SearchDropdown";
import {ConstantUtils} from "../../../../../utils/ConstantUtils";

interface BasicInfoEditProps {
  profile: ProfileType;
  setResume: Dispatch<SetStateAction<ResumeType>>;
  resumeTabs: ResumeTabsType;
}

const BasicInfoEdit = (props: BasicInfoEditProps) => {
  const { profile, setResume, resumeTabs } = props;
  // 생년월일
  const imageInputRef = useRef<HTMLInputElement>(null);
  const localNameRef = useRef<HTMLInputElement>(null);
  const firstPhoneNumberRef = useRef<HTMLSelectElement>(null);
  const middlePhoneNumberRef = useRef<HTMLInputElement>(null);
  const lastPhoneNumberRef = useRef<HTMLInputElement>(null);
  const emailIdRef = useRef<HTMLInputElement>(null);
  const emailAddressRef = useRef<HTMLSelectElement>(null);
  const token = useRecoilValue(IvyAuthorizationTokenAtom);
  const COMPLETE_PHONE_NUMBER_LENGTH = 11;
  const EMAIL_ADDRESS_LIST = ['', '@naver.com', '@google.com', '@daum.net'];
  const [isEmailInputMode, setIsEmailInputMode] = useState<boolean>(false);
  const [isAddressInputMode, setIsAddressInputMode] = useState<boolean>(false);
  const [emailAddressOption, setEmailAddressOption] = useState<string>('');
  const DAUM_POST_CODE_URL = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';

  // 입력폼 포커스 유무
  const [isLocalNameFocused, setLocalNameFocused] = useState(false);
  const [isEnNameFocused, setEnNameFocused] = useState(false);
  const [isAddressDetailFocused, setIsAddressDetailFocused] = useState(false);

  const openPostcodePopupHook = useDaumPostcodePopup(DAUM_POST_CODE_URL);

  const openDaumPostcodePopup = () => {
    openPostcodePopupHook({ onComplete: handlePostcodeComplete });
  };

  const handlePostcodeComplete = (data: any) => {
    let fullAddress = data.address;
    let extraAddress = '';

    if (data.addressType === 'R') {
      if (data.bname !== '') {
        extraAddress += data.bname;
      }
      if (data.buildingName !== '') {
        extraAddress += extraAddress !== '' ? `, ${data.buildingName}` : data.buildingName;
      }
      fullAddress += extraAddress !== '' ? ` (${extraAddress})` : '';
    }

    if (!_.isEmpty(fullAddress)) {
      updateField('address', fullAddress);
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      uploadFile(event.target.files[0]);
      event.target.value = ''; // 두번쨰 업로드를 위해 초기화 시켜준다.
    }
  };

  const getEmailAddressOption = (value: string) => {
    return EMAIL_ADDRESS_LIST.indexOf(value) >= 0 ? value : 'direct';
  };

  const uploadFile = async (file: File) => {
    await uploadImage(token, file)
      .then((res) => {
        if (!_.isEmpty(res.mediaUrl)) {
          updateField('profileImageUuid', res.uuid);
          updateImgField(res);
        } else {
          alert('이미지 업로드 에러!');
        }
      })
      .catch((e) => console.error(e));
  };

  const handleUploadClick = () => {
    if (imageInputRef.current) {
      imageInputRef.current.click();
    }
  };

  const updateNationField = (index: number, value: string) => {
    updateField('nation', value);
  };

  const updateField = (fieldName: keyof ProfileType, value: string) => {
    setResume((prevResume) => ({
      ...prevResume,
      profile: { ...prevResume.profile, [fieldName]: value },
    }));
  };

  const updateImgField = (value: MedeaType) => {
    setResume((prevResume) => ({
      ...prevResume,
      profile: { ...prevResume.profile, profileImageUrl: value },
    }));
  };

  const updateBirthDayField = (value: Date) => {
    updateField('birthday', DateTimeUtils.toDateString(value));
  };

  const updatePhoneNumberField = () => {
    const first = firstPhoneNumberRef.current?.value || '010';
    const middle = middlePhoneNumberRef.current?.value || '';
    const last = lastPhoneNumberRef.current?.value || '';
    const phoneNumber = first.concat(middle.concat(last));

    if (phoneNumber.length === COMPLETE_PHONE_NUMBER_LENGTH) {
      updateField('phoneNumber', phoneNumber);
    } else {
      // 휴대전화는 11자리가 아니면 empty로 처리
      updateField('phoneNumber', '');
    }
  };

  const updateEmailField = (id: string, address: string) => {
    if (!_.isEmpty(id) && !_.isEmpty(address)) {
      const email = id.concat(address);
      updateField('email', email);
    } else {
      // 이메일은 둘 중 하나라도 입력하지 않으면 empty로 처리
      updateField('email', '');
    }
  };

  const blurEventHandle = (setFocused: Function, e: FocusEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      setFocused(false);
    } else {
      setFocused(true);
    }
  };

  const checkInputValue = () => {
    if (profile.localName !== '') {
      setLocalNameFocused(true);
    }

    if (profile.enName !== '') {
      setEnNameFocused(true);
    }

    if (profile.addressDetail !== '') {
      setIsAddressDetailFocused(true);
    }
  };

  useEffect(() => {
    AuthUtils.isLoggedInAndRedirect(token, 'login');
  }, [token]);

  useEffect(() => {
    setEmailAddressOption(profile.emailAddress);
    setIsEmailInputMode(EMAIL_ADDRESS_LIST.indexOf(profile.emailAddress) >= 0);
    checkInputValue();
  }, []);

  if (profile === undefined) {
    return null;
  }

  return (
    <>
      <section
        ref={resumeTabs[0].element}
        className={`${C.basic_edit_section} ${S.basic_edit_wrapper} wrapper`}
      >
        <h4 className={` ${S.basic_edit_title} h4`}>인적사항</h4>
        <div className={S.basic_input_box}>
          {/* 이력서 사진 */}
          <div className={`${C.basic_edit_input} ${S.image_edit_input}`}>
            {profile?.profileImageUrl?.mediaUrl ? (
              <img
                onClick={() => updateImgField({} as MedeaType)}
                className={S.remove_icon}
                src={removeIcon}
                alt="사진 삭제"
              />
            ) : (
              <></>
            )}
            <div onClick={handleUploadClick} className={S.img_upload_box}>
              <input
                type={'file'}
                ref={imageInputRef}
                accept={'jpeg'}
                style={{ display: 'none' }} // @limi display: none 으로 넣어주세요
                onChange={(e) => handleImageChange(e)}
              />
              {profile?.profileImageUrl?.mediaUrl ? (
                <img src={profile.profileImageUrl.mediaUrl} className={S.uploaded_img}></img>
              ) : (
                <>
                  <div className={S.default_upload_box}></div>
                  <div className={`b4 ${S.profile_img_info}`}>
                    권장 사이즈: 150X210
                    <br />
                    jpg, jpeg, png 이미지 파일만 등록 가능
                  </div>
                </>
              )}
            </div>
          </div>
          {/* 사진 등록 영역 제외한 부분 */}
          <div className={S.side_grp_wrap}>
            {/* 성명(국문) */}
            <div className="input-grp">
              <label
                htmlFor="localName"
                className={isLocalNameFocused ? 'input-label focus-on' : 'input-label'}
              >
                성명 (국문)
                <span className={C.red_dot}></span>
              </label>
              <input
                type="text"
                id="localName"
                ref={localNameRef}
                className={`input-text input-border ${S.name_width}`}
                onFocus={() => {
                  setLocalNameFocused(true);
                }}
                onBlur={(e) => {
                  blurEventHandle(setLocalNameFocused, e);
                }}
                onInput={(e) => {
                  InputUtils.onlyKorAndEngAndSpaceCanInput(e);
                }}
                onChange={(e) => {
                  updateField('localName', e.target.value);
                  setLocalNameFocused(true);
                }}
                value={profile.localName}
              />
            </div>
            {/* 성명(영문) */}
            <div className={`input-grp ${S.name_width}`}>
              <label
                htmlFor="enName"
                className={isEnNameFocused ? 'input-label focus-on' : 'input-label'}
              >
                성명 (영문)
                <span className={C.red_dot}></span>
              </label>
              <input
                type="text"
                id="enName"
                className={`input-text input-border ${S.name_width}`}
                onFocus={() => {
                  setEnNameFocused(true);
                }}
                onBlur={(e) => {
                  blurEventHandle(setEnNameFocused, e);
                }}
                onInput={(e) => InputUtils.onlyKorAndEngAndSpaceCanInput(e)}
                onChange={(e) => {
                  updateField('enName', e.target.value);
                  setEnNameFocused(true);
                }}
                value={profile.enName}
              />
            </div>
            {/* 이메일 */}
            <div className={`${C.basic_edit_input} ${S.email_edit_input}`}>
              <input
                type="text"
                className="input-text input-border"
                value={profile.emailId}
                ref={emailIdRef}
                onChange={(e) => {
                  const value = e.target.value;
                  const regex = /^[a-zA-Z0-9.]*$/;
                  if (!regex.test(value)) {
                    // 입력된 값에서 영문자, 숫자, 마침표 이외의 문자를 제거합니다.
                    e.target.value = value.replace(/[^a-zA-Z0-9.]/g, '');
                  }
                  updateField('emailId', e.target.value);
                  updateEmailField(e.target.value, profile.emailAddress);
                }}
              />
              <p className="b2">@</p>
              <input
                type="text"
                className="input-text input-border"
                value={
                  _.isEmpty(profile.emailAddress)
                    ? ''
                    : profile.emailAddress.indexOf('@') >= 0
                    ? profile.emailAddress.slice(1)
                    : profile.emailAddress
                }
                readOnly={!isEmailInputMode}
                onChange={(e) => {
                  if (isEmailInputMode) {
                    let editedEmailAddress = e.target.value;
                    if (profile.emailAddress.indexOf('@') <= 0) {
                      editedEmailAddress = '@' + editedEmailAddress;
                    }
                    updateField('emailAddress', editedEmailAddress);
                    updateEmailField(profile.emailId, editedEmailAddress);
                  }
                }}
              />
              <select
                name="email"
                id="email"
                className="input-border input-select"
                value={getEmailAddressOption(emailAddressOption)}
                ref={emailAddressRef}
                onChange={(e) => {
                  if (e.target.value === 'direct') {
                    setIsEmailInputMode(true);
                    setEmailAddressOption('direct');
                    updateField('emailAddress', '');
                    updateEmailField(profile.emailId, '');
                  } else {
                    setIsEmailInputMode(false);
                    setEmailAddressOption(e.target.value);
                    updateField('emailAddress', e.target.value);
                    updateEmailField(profile.emailId, e.target.value);
                  }
                }}
              >
                <option value="">선택하세요</option>
                <option value="@naver.com">naver.com</option>
                <option value="@gmail.com">gmail.com</option>
                <option value="@daum.net">daum.net</option>
                <option value="direct">직접입력</option>
              </select>
            </div>
            {/* 생년월일 */}
            <div className={`${C.basic_edit_input} ${C.resume_period_date}`}>
              <SelectDateBox
                selectedDate={DateTimeUtils.stringToDateType(profile.birthday, 'yyyy-MM-dd')}
                setSelectedDate={updateBirthDayField}
                type={'birth-day'}
              />
            </div>
            {/* 국적 */}
            <SearchDropdown
                selectedSearchValue={ConstantUtils.countryCodeToName(profile.nation)}
                setSelectedSearchValue={updateNationField}
                type={'nation'}
                token={token}
                selectedValueIndex={0}
            />
            {/* 성별 */}
            <select
              name="gender"
              id="gender"
              className={`input-border input-select ${S.gender_select}`}
              value={profile.gender}
              onChange={(e) => updateField('gender', e.target.value)}
            >
              <option value="">성별선택 *</option>
              <option value="MEN">남</option>
              <option value="WOMEN">여</option>
            </select>

              {/* 연락처 */}
            <div className={`${C.basic_edit_input} ${S.phone_edit_input}`}>
              <select
                name="phoneNum"
                id="phoneNum"
                className="input-border input-select"
                value={profile.firstOfPhoneNumber}
                ref={firstPhoneNumberRef}
                onChange={(e) => {
                  updateField('firstOfPhoneNumber', e.target.value);
                  updatePhoneNumberField();
                }}
              >
                <option value="010">010</option>
                <option value="011">011</option>
                <option value="016">016</option>
                <option value="070">070</option>
              </select>
              <p className="b2">-</p>
              <input
                type="text"
                className="input-text input-border"
                maxLength={4}
                onInput={(e) => InputUtils.onlyNumberCanInput(e)}
                ref={middlePhoneNumberRef}
                value={profile.middleOfPhoneNumber}
                onChange={(e) => {
                  updateField('middleOfPhoneNumber', e.target.value);
                  updatePhoneNumberField();
                }}
              />
              <p className="b2">-</p>
              <input
                type="text"
                className="input-text input-border"
                maxLength={4}
                onInput={(e) => InputUtils.onlyNumberCanInput(e)}
                ref={lastPhoneNumberRef}
                value={profile.lastOfPhoneNumber}
                onChange={(e) => {
                  updateField('lastOfPhoneNumber', e.target.value);
                  updatePhoneNumberField();
                }}
              />
            </div>
            {/* 주소 */}
            <div className={S.address_search_wrap}>
              <div className={`${S.address_edit_input} ${C.search_bar} ${C.search_border}`}>
                <input
                  type="text"
                  className={`input-text ${C.search_input}`}
                  placeholder="주소"
                  readOnly
                  value={profile.address}
                  onChange={(e) => updateField('address', e.currentTarget.value)}
                />
                <button className={C.search_icon} onClick={openDaumPostcodePopup}></button>
              </div>
              <div className={`input-grp ${S.detail_adress} ${C.search_bar} ${C.search_border}`}>
                <label
                  htmlFor="addressDetail"
                  className={isAddressDetailFocused ? 'input-label focus-on' : 'input-label'}
                >
                  상세 주소
                  <span className={C.red_dot}></span>
                </label>
                <input
                  type="text"
                  id="addressDetail"
                  className={`input-text detail-address-input ${C.search_input}`}
                  onFocus={() => {
                    setIsAddressDetailFocused(true);
                  }}
                  onBlur={(e) => {
                    blurEventHandle(setIsAddressDetailFocused, e);
                  }}
                  onChange={(e) => {
                    updateField('addressDetail', e.currentTarget.value);
                    setIsAddressDetailFocused(true);
                  }}
                  value={profile.addressDetail}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <hr />
    </>
  );
};

export default BasicInfoEdit;
